import { track } from "@/lib/analytics";
import { studyFunnelSignal } from "@/lib/study-conversion";
import { classifySignupError } from "@/lib/signup-errors";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { lovable } from "@/integrations/lovable/index";
import { signInWithApple } from "@/lib/apple-auth";
import { nativePlatform } from "@/lib/store-billing";
import { OAUTH_SITE_URL } from "@/lib/oauth";

/**
 * Android builds packaged from local files (origin https://localhost) cannot
 * reach the hosted sign-in routes (/~oauth/*), which then render a 404.
 * Hand sign-in over to the live site so the flow completes there.
 */
function leaveLocalAndroidOrigin(): boolean {
  if (typeof window === "undefined") return false;
  if (nativePlatform() !== "google") return false;
  if (window.location.origin === OAUTH_SITE_URL) return false;
  window.location.replace(`${OAUTH_SITE_URL}/login`);
  return true;
}

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Mail, Lock, Sparkles } from "lucide-react";


export const Route = createFileRoute("/login")({
  component: LoginPage,
  head: () => ({
    meta: [
      { title: "Sign In - Inquira" },
      { name: "description", content: "Sign in to Inquira to access AI-powered step-by-step problem solving." },
      { property: "og:title", content: "Sign In - Inquira" },
      { property: "og:description", content: "Sign in to Inquira to access AI-powered step-by-step problem solving." },
      { property: "og:url", content: "https://www.inquira.online/login" },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/login" }],
  }),
});

function LoginPage() {
  const { t } = useTranslation();
  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(false);
  useEffect(() => {
    try {
      if (sessionStorage.getItem("inquira.signup.mode") === "free") {
        setIsSignUp(true);
        sessionStorage.removeItem("inquira.signup.mode");
      }
    } catch { /* Existing sign-in works without browser storage. */ }
  }, []);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [referralCode, setReferralCode] = useState("");
  const [showReferral, setShowReferral] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("ref");
    if (fromUrl) {
      try { window.localStorage.setItem("inquira_ref", fromUrl.toUpperCase()); } catch { /* ignore */ }
      setReferralCode(fromUrl.toUpperCase());
      return;
    }
    try {
      const stored = window.localStorage.getItem("inquira_ref") ?? "";
      setReferralCode(stored);
      if (stored) setShowReferral(true);
    } catch { /* optional attribution */ }
  }, []);

  const applyReferral = async (code: string) => {
    if (!code) return;
    try {
      await supabase.rpc("apply_referral_code" as never, { _code: code } as never);
      try { window.localStorage.removeItem("inquira_ref"); } catch { /* ignore */ }
    } catch (e) {
      console.warn("Referral apply failed", e);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);
    if (isSignUp) studyFunnelSignal("signup_attempt");

    const result = isSignUp
      ? await signUp(email, password)
      : await signIn(email, password);

    setLoading(false);

    if (result.error) {
      setError(t(classifySignupError(result.error)));
      void track(isSignUp ? "signup_failed" : "login_failed", { method: "email", reason: classifySignupError(result.error) });
    } else if (isSignUp) {
      // Best-effort: try applying now (works if email confirmations are off).
      // Otherwise it will apply on first sign-in via the useEffect below.
      await applyReferral(referralCode);
      void track("signup", { method: "email" });
      if (result.hasSession) {
        // Email confirmation disabled: the account is live — go straight to
        // the tutor instead of leaving the new user on a dead-end message.
        navigate({ to: "/" });
      } else {
        setSuccess(t("login.confirmEmail"));
      }
    } else {
      await applyReferral(referralCode);
      void track("login", { method: "email" });
      navigate({ to: "/" });
    }
  };

  const handleGoogleSignIn = async () => {
    if (leaveLocalAndroidOrigin()) return;
    setError(null);
    setLoading(true);
    if (isSignUp) studyFunnelSignal("signup_attempt");
    try {
      // Google is a Lovable-managed provider: its client ID/secret live in the
      // managed OAuth broker, not in the project's own provider settings. The
      // direct Supabase /authorize call used by the native deep-link flow has no
      // secret there and fails with "missing OAuth secret", so every platform
      // (including the iOS/Android wrappers, which load the real https site)
      // must go through the managed helper below.

      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      if (result.error) {
        setError(t("auth.googleFailed"));
        setLoading(false);
        void track(isSignUp ? "signup_failed" : "login_failed", { method: "google" });
      }
      // If the flow redirected, we only get here when the user comes back
      // without a session — clear the spinner so the button isn't stuck.
      if (result.redirected) {
        setLoading(false);
        return;
      }
      await applyReferral(referralCode);
      void track("login", { method: "google" });
      navigate({ to: "/" });
    } catch {
      setError(t("auth.googleFailed"));
      setLoading(false);
      void track(isSignUp ? "signup_failed" : "login_failed", { method: "google" });
    }
  };

  /**
   * Sign in with Apple. On iOS this opens Apple's real native authorization
   * sheet; on web/Android it uses the managed Apple OAuth provider. Errors are
   * mapped to localized copy — Apple's raw tokens never reach the UI or logs.
   */
  const handleAppleSignIn = async () => {
    if (leaveLocalAndroidOrigin()) return;
    setError(null);
    setSuccess(null);
    setLoading(true);
    if (isSignUp) studyFunnelSignal("signup_attempt");
    const outcome = await signInWithApple(window.location.origin);
    if (outcome.status === "redirected") return;
    if (outcome.status === "canceled") {
      setError(t("auth.appleErrors.canceled"));
      setLoading(false);
      void track(isSignUp ? "signup_failed" : "login_failed", { method: "apple", reason: "canceled" });
      return;
    }
    if (outcome.status === "error") {
      setError(t(`auth.appleErrors.${outcome.code}`));
      setLoading(false);
      void track(isSignUp ? "signup_failed" : "login_failed", { method: "apple", reason: outcome.code });
      return;
    }
    await applyReferral(referralCode);
    void track("login", { method: "apple" });
    navigate({ to: "/" });
  };


  return (
    <div className="min-h-screen notebook-lines flex items-center justify-center px-4">
      <div className="w-full max-w-sm space-y-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink transition-colors">
          <ArrowLeft className="size-4" /> {t("login.backToHome")}
        </Link>

        <div className="text-center space-y-2">
          <div className="size-16 mx-auto rounded-full bg-neon-pink/10 flex items-center justify-center">
            <Sparkles className="size-8 text-neon-pink" />
          </div>
          <h1 className="font-[family-name:var(--font-display)] font-extrabold text-2xl text-ink">
            {isSignUp ? t("login.createAccount") : t("login.welcomeBack")}
          </h1>
          <p className="text-sm text-ink-muted">
            {isSignUp ? t("login.signUpSubtitle") : t("login.signInSubtitle")}
          </p>
        </div>


        <div className="bg-card rounded-2xl p-6 border border-rule shadow-sm space-y-4">
          {error && (
            <div className="bg-destructive/10 text-destructive text-sm rounded-xl p-3 border border-destructive/20">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-neon-blue/10 text-ink text-sm rounded-xl p-3 border border-neon-blue/20">
              {success}
            </div>
          )}

          {/* Apple requires Sign in with Apple to be offered alongside other
              third-party logins, at equal prominence (App Review 4.8). */}
          <button
            type="button"
            onClick={handleAppleSignIn}
            disabled={loading}
            aria-label={t("login.continueWithApple")}
            className="w-full h-12 rounded-xl bg-black text-white flex items-center justify-center gap-3 text-[17px] font-medium font-[family-name:var(--font-display)] disabled:opacity-60 transition-opacity hover:opacity-90"
          >
            <svg className="size-5" viewBox="0 0 384 512" aria-hidden="true" fill="currentColor">
              <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
            </svg>
            {t("login.continueWithApple")}
          </button>

          <Button
            type="button"
            variant="outline"
            size="full"
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="font-[family-name:var(--font-display)] gap-3"
          >
            <svg className="size-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {t("login.continueWithGoogle")}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-rule" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-ink-muted">{t("login.or")}</span>
            </div>
          </div>


          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-ink-muted">{t("login.email")}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-ink-muted" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full bg-paper border border-rule rounded-xl py-3 pl-10 pr-4 text-sm text-ink placeholder:text-ink-muted/50 focus:outline-none focus:ring-2 focus:ring-neon-blue/30"
                  placeholder={t("login.emailPlaceholder")}
                />
              </div>

            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-ink-muted">{t("login.password")}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-ink-muted" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full bg-paper border border-rule rounded-xl py-3 pl-10 pr-4 text-sm text-ink placeholder:text-ink-muted/50 focus:outline-none focus:ring-2 focus:ring-neon-blue/30"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {isSignUp && !showReferral && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowReferral(true)}
                  className="text-xs text-ink-muted hover:text-ink font-medium transition-colors"
                >
                  {t("login.haveReferralCode")}
                </button>
              </div>
            )}

            {isSignUp && showReferral && (
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-ink-muted">
                  {t("login.referralCode")}
                </label>
                <input
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  maxLength={12}
                  className="w-full bg-paper border border-rule rounded-xl py-3 px-4 text-sm text-ink placeholder:text-ink-muted/50 focus:outline-none focus:ring-2 focus:ring-neon-pink/30 uppercase tracking-widest"
                  placeholder="FRIEND8"
                />
                <p className="text-[11px] text-ink-muted">
                  {t("login.referralHint")}
                </p>
              </div>
            )}


            <Button
              type="submit"
              variant="scan"
              size="full"
              disabled={loading}
              className="font-[family-name:var(--font-display)]"
            >
              {loading ? t("login.pleaseWait") : isSignUp ? t("login.createAccount") : t("common.signIn")}
            </Button>
          </form>

          {!isSignUp && (
            <div className="text-center">
              <button
                type="button"
                onClick={async () => {
                  if (!email) {
                    setError(t("login.enterEmailFirst"));
                    return;
                  }
                  setError(null);
                  const { error } = await supabase.auth.resetPasswordForEmail(email, {
                    redirectTo: `${window.location.origin}/reset-password`,
                  });
                  if (error) {
                    setError(error.message);
                  } else {
                    setSuccess(t("login.resetSent"));
                  }
                }}
                className="text-xs text-ink-muted hover:text-ink font-medium transition-colors"
              >
                {t("login.forgotPassword")}
              </button>

            </div>
          )}

          <div className="text-center">
            <button
              type="button"
              onClick={() => { setIsSignUp(!isSignUp); setError(null); setSuccess(null); }}
              className="text-sm text-neon-pink hover:text-neon-pink/80 font-medium transition-colors"
            >
              {isSignUp ? t("login.toggleToSignIn") : t("login.toggleToSignUp")}
            </button>
          </div>
        </div>

        <div className="text-center text-xs text-ink-muted">
          {t("login.planFootnote")}
        </div>

      </div>
    </div>
  );
}
